'use client'

import { useEffect, useRef, useState } from 'react'

import { GameShell } from '@/components/losi-games/game-shell'
import { AdaptivePongAi } from '@/lib/losi-games/adaptiveAi'
import { predictPaddleY } from '@/lib/losi-games/pongAi'
import { mountGameCanvas } from '@/lib/losi-games/canvas'
import { difficultyScale, type Difficulty } from '@/lib/losi-games/difficulty'
import { drawEyeCapsule, drawInkDot } from '@/lib/losi-games/glyphs'
import { bestLocalScore, recordScoreAndWhisper } from '@/lib/losi-games/leaderboard'
import { resolveGameTheme } from '@/lib/losi-games/theme'
import type { LosiGameRankWhisper } from '@/lib/losi-games/types'

export function EyePongGame({ colorId = 'creme' }: { colorId?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [score, setScore] = useState(0)
  const [best, setBest] = useState(0)
  const [over, setOver] = useState(false)
  const [menu, setMenu] = useState(true)
  const [aiLabel, setAiLabel] = useState('Warming up')
  const [whisper, setWhisper] = useState<LosiGameRankWhisper | null>(null)
  const [theme, setTheme] = useState(() => resolveGameTheme(colorId))
  const themeRef = useRef(theme)
  const startFn = useRef<(d: Difficulty) => void>()
  const restartFn = useRef<() => void>()

  useEffect(() => {
    const next = resolveGameTheme(colorId)
    themeRef.current = next
    setTheme(next)
  }, [colorId])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    themeRef.current = resolveGameTheme(colorId)
    let cleanup: (() => void) | undefined

    setBest(bestLocalScore('ping-pong'))
    const { ctx, size, dispose } = mountGameCanvas(canvas)
    const ai = new AdaptivePongAi()
    setAiLabel(ai.label())

    let padHalfH = 40
    let padHalfW = 11
    let ballR = 8
    let inset = 32
    const syncMetrics = () => {
      const s = size()
      padHalfH = Math.max(40, s.h * 0.1)
      padHalfW = Math.max(11, s.w * 0.015)
      ballR = Math.max(8, Math.min(s.w, s.h) * 0.016)
      inset = Math.max(32, s.w * 0.04)
    }
    syncMetrics()
    let last = performance.now()
    let pointerY = 0
    let scoreAcc = 0
    let gameOver = false
    let playing = false
    let diffScale = 1
    /** Hits this exchange — ball ramps while the rally lives. */
    let volley = 0
    let { w, h } = size()
    pointerY = h / 2
    const left = { x: inset, y: h / 2 }
    const right = { x: w - inset, y: h / 2 }
    const ball = { x: w / 2, y: h / 2, vx: 280, vy: 80 }
    let alive = true
    let raf = 0

    const onMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect()
      pointerY = e.clientY - rect.top
    }
    canvas.addEventListener('pointermove', onMove)

    const serve = (dir: 1 | -1) => {
      ;({ w, h } = size())
      ball.x = w / 2
      ball.y = h / 2
      volley = 0
      const tune = ai.tuning()
      // Court-relative serve — matches studio EyePong.
      const base = Math.max(420, w * 0.48)
      ball.vx = (base + tune.skill * 50) * diffScale * dir
      ball.vy = (Math.random() * 2 - 1) * (140 + tune.skill * 50) * diffScale
      setAiLabel(ai.label())
    }

    const endRun = () => {
      if (gameOver) return
      gameOver = true
      playing = false
      setOver(true)
      setWhisper(recordScoreAndWhisper('ping-pong', scoreAcc))
      setBest(bestLocalScore('ping-pong'))
    }

    startFn.current = (d: Difficulty) => {
      diffScale = difficultyScale(d)
      ai.setDifficulty(d)
      scoreAcc = 0
      setScore(0)
      gameOver = false
      setOver(false)
      setMenu(false)
      setWhisper(null)
      playing = true
      serve(1)
    }

    restartFn.current = () => {
      setMenu(true)
      setOver(false)
      playing = false
      setBest(bestLocalScore('ping-pong'))
    }

    // Swept face-crossing hit so a fast ball or fast drag can't tunnel through.
    const hitPaddle = (
      px: number,
      py: number,
      prevY: number,
      prevX: number,
      fromLeft: boolean,
      stepDt: number
    ) => {
      const face = fromLeft ? px + padHalfW : px - padHalfW
      const going = fromLeft ? ball.vx < 0 : ball.vx > 0
      if (!going) return false
      const edgeBefore = fromLeft ? prevX - ballR : prevX + ballR
      const edgeAfter = fromLeft ? ball.x - ballR : ball.x + ballR
      const crossedFace = fromLeft
        ? edgeBefore > face && edgeAfter <= face
        : edgeBefore < face && edgeAfter >= face
      const overlapping = fromLeft
        ? ball.x - ballR <= face && ball.x + ballR >= px - padHalfW
        : ball.x + ballR >= face && ball.x - ballR <= px + padHalfW
      if (!crossedFace && !overlapping) return false
      const sweep = Math.abs(py - prevY) + 4
      const half = padHalfH + ballR + sweep
      const y0 = ball.y - ball.vy * stepDt
      let hitY = ball.y
      if (crossedFace && Math.abs(edgeAfter - edgeBefore) > 1e-6) {
        const t = Math.max(0, Math.min(1, (face - edgeBefore) / (edgeAfter - edgeBefore)))
        hitY = y0 + (ball.y - y0) * t
      }
      if (Math.abs(hitY - py) > half && Math.abs(hitY - prevY) > half) return false
      volley += 1
      const offset = (hitY - py) / padHalfH
      const tune = ai.tuning()
      // Longer rally → faster ball (cap keeps substep collision honest).
      const ramp = 1 + volley * 0.055
      const cap = Math.min(920, 500 + tune.skill * 140 + volley * 36)
      const speed = Math.min(
        cap,
        Math.max(Math.hypot(ball.vx, ball.vy) * 1.05 + 10, Math.max(420, w * 0.48) * diffScale * ramp) +
          volley * 5
      )
      const ang = offset * 0.55
      ball.vx = Math.cos(ang) * speed * (fromLeft ? 1 : -1)
      ball.vy = Math.sin(ang) * speed + offset * 36
      ball.x = fromLeft ? px + padHalfW + ballR + 1 : px - padHalfW - ballR - 1
      return true
    }

    const loop = (now: number) => {
      if (!alive) return
      const dt = Math.min(0.033, (now - last) / 1000)
      last = now
      ;({ w, h } = size())
      syncMetrics()
      const th = themeRef.current
      left.x = inset
      right.x = w - inset

      if (!gameOver && playing) {
        const tune = ai.tuning()
        const leftPrevY = left.y
        const rightPrevY = right.y
        left.y += (pointerY - left.y) * Math.min(1, dt * 16)
        left.y = Math.max(padHalfH + 6, Math.min(h - padHalfH - 6, left.y))

        const commitAt = w * tune.commitX
        let aiTarget = h / 2
        if (ball.vx > 0 && ball.x >= commitAt) {
          const pred = predictPaddleY(ball, right.x, 0, h, ballR)
          if (pred != null) {
            if (Math.random() < tune.missChance) {
              aiTarget =
                pred +
                (Math.random() > 0.5 ? 1 : -1) * (padHalfH * 2.4 + tune.aimError * 0.8)
            } else {
              const fluff =
                Math.random() < tune.fluff ? (Math.random() * 2 - 1) * tune.aimError * 1.6 : 0
              aiTarget = pred + Math.sin(now / 310) * tune.aimError * 0.55 + fluff
            }
          } else aiTarget = ball.y
        } else if (ball.vx <= 0) {
          aiTarget = h / 2 + Math.sin(now / 900) * 28
        }
        const dy = aiTarget - right.y
        right.y += Math.sign(dy) * Math.min(Math.abs(dy), tune.maxSpeed * tune.chase * dt)
        right.y = Math.max(padHalfH + 6, Math.min(h - padHalfH - 6, right.y))

        const steps = Math.max(1, Math.ceil((Math.hypot(ball.vx, ball.vy) * dt) / (ballR * 0.85)))
        const sdt = dt / steps
        for (let s = 0; s < steps; s++) {
          const prevX = ball.x
          const stepLeft = leftPrevY + (left.y - leftPrevY) * ((s + 1) / steps)
          const stepRight = rightPrevY + (right.y - rightPrevY) * ((s + 1) / steps)
          const prevLeft = leftPrevY + (left.y - leftPrevY) * (s / steps)
          const prevRight = rightPrevY + (right.y - rightPrevY) * (s / steps)
          ball.x += ball.vx * sdt
          ball.y += ball.vy * sdt
          if (ball.y < ballR) {
            ball.y = ballR
            ball.vy = Math.abs(ball.vy)
          }
          if (ball.y > h - ballR) {
            ball.y = h - ballR
            ball.vy = -Math.abs(ball.vy)
          }
          if (hitPaddle(left.x, stepLeft, prevLeft, prevX, true, sdt)) {
            scoreAcc += 1
            setScore(scoreAcc)
            ai.onPlayerHit()
            setAiLabel(ai.label())
          }
          hitPaddle(right.x, stepRight, prevRight, prevX, false, sdt)
        }

        if (ball.x < -30) {
          ai.onAiGoal()
          endRun()
        } else if (ball.x > w + 30) {
          ai.onPlayerGoal()
          scoreAcc += 1
          setScore(scoreAcc)
          serve(-1)
        }
      }

      ctx.fillStyle = th.paper
      ctx.fillRect(0, 0, w, h)
      ctx.strokeStyle = th.mute
      ctx.setLineDash([5, 8])
      ctx.beginPath()
      ctx.moveTo(w / 2, 10)
      ctx.lineTo(w / 2, h - 10)
      ctx.stroke()
      ctx.setLineDash([])
      drawEyeCapsule(ctx, left.x, left.y, padHalfH, padHalfW, 0, th.body)
      drawEyeCapsule(ctx, right.x, right.y, padHalfH, padHalfW, 0, th.body)
      if (!gameOver) drawInkDot(ctx, ball.x, ball.y, ballR, th.mark)

      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    cleanup = () => {
      alive = false
      cancelAnimationFrame(raf)
      dispose()
      canvas.removeEventListener('pointermove', onMove)
    }

    return () => {
      cleanup?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <GameShell
      theme={theme}
      title="Ping Pong"
      score={score}
      best={best}
      meta={`AI ${aiLabel}`}
      menu={menu}
      over={over}
      whisper={whisper}
      hint="Snappy serves. Easy AI misses. Miss ends the run."
      onStart={(d) => startFn.current?.(d)}
      onRestart={() => restartFn.current?.()}
    >
      <canvas ref={canvasRef} className="block touch-none" />
    </GameShell>
  )
}
